import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getTicketRequestSubCategories,
  getTicketRequestSubCategoriesByCategoryId,
} from 'api/ticketRequestSubCategories';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';

type SubcategoryIRequestT = {
  isEnabled: boolean;
  roofTopId: string | undefined;
  oemFamilyId: string | undefined;
};

type SubcategoryByCategoryIdsRequestT = {
  isEnabled: boolean;
  categoryIds: number[];
  oemFamilyId: string | undefined;
};

type SubcategoryByCategoryIdRequestT = {
  isEnabled: boolean;
  categoryId: number | undefined;
  oemFamilyId: string | undefined;
};

export const useFetchTicketRequestSubcategories = (
  params: SubcategoryIRequestT
): ReturnType<typeof useQuery<TicketRequestSubcategoryI[], ApiErrorI>> => {
  return useQuery<TicketRequestSubcategoryI[], ApiErrorI>(
    [RequestQueryKeysE.FetchTicketRequestSubcategories, params.roofTopId, params.oemFamilyId],
    async (): Promise<TicketRequestSubcategoryI[]> =>
      getTicketRequestSubCategories(params.oemFamilyId),
    {
      enabled: params.isEnabled,
      staleTime: RequestStaleTimeE.TicketRequestCategories,
    }
  );
};

export const useFetchTicketRequestSubcategoriesByCategoryIds = (
  params: SubcategoryByCategoryIdsRequestT
): {
  data: TicketRequestSubcategoryI[] | undefined;
  isLoading: boolean;
  error: ApiErrorI | null;
} => {
  const queries = useQueries({
    queries: params.categoryIds.map(
      (
        categoryId
      ): {
        queryKey: unknown[];
        queryFn: () => Promise<TicketRequestSubcategoryI[]>;
        enabled: boolean;
      } => ({
        queryKey: [
          RequestQueryKeysE.FetchTicketRequestSubcategories,
          'byCategoryId',
          categoryId,
          params.oemFamilyId,
        ],
        queryFn: (): Promise<TicketRequestSubcategoryI[]> =>
          getTicketRequestSubCategoriesByCategoryId(categoryId, params.oemFamilyId),
        enabled: params.isEnabled && params.categoryIds.length > 0,
      })
    ),
  });

  const isLoading = queries.some((q): boolean => q.isLoading);
  const error =
    (queries.find((q): boolean => q.error != null)?.error as ApiErrorI | undefined) ?? null;
  const allData = queries
    .filter((q): boolean => q.data != null)
    .flatMap((q): TicketRequestSubcategoryI[] => q.data ?? []);
  const dataIdsKey = allData
    .map((item): number => item.id)
    .sort((a, b): number => a - b)
    .join(',');
  const data = useMemo(
    (): TicketRequestSubcategoryI[] | undefined =>
      allData.length > 0
        ? Array.from(
            new Map(
              allData.map((item): [number, TicketRequestSubcategoryI] => [item.id, item])
            ).values()
          )
        : undefined,
    // dataIdsKey only: allData is a new array every render; including it would break stable reference and cause infinite loops in consumers
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataIdsKey]
  );

  return { data, isLoading, error: error ?? null };
};

export const useFetchTicketRequestSubcategoriesByCategoryId = (
  params: SubcategoryByCategoryIdRequestT
): ReturnType<typeof useQuery<TicketRequestSubcategoryI[], ApiErrorI>> => {
  return useQuery<TicketRequestSubcategoryI[], ApiErrorI>(
    [RequestQueryKeysE.FetchTicketRequestSubcategories, params.categoryId, params.oemFamilyId],
    (): Promise<TicketRequestSubcategoryI[]> =>
      params.categoryId !== undefined
        ? getTicketRequestSubCategoriesByCategoryId(params.categoryId, params.oemFamilyId)
        : Promise.resolve([]),
    {
      enabled: params.isEnabled && params.categoryId !== undefined,
      staleTime: RequestStaleTimeE.TicketRequestCategories,
    }
  );
};
